import { h } from 'preact'
import { useSignal } from '@preact/signals'
import { PresenceDot } from './PresenceDot.jsx'
import { LastSeen } from './LastSeen.jsx'
import { InConversationSearch } from './InConversationSearch.jsx'
import { edit_chat_room_path } from '../../../routes'
import { leaveRoom, purgeRoom } from '../leave-room.js'
import { t } from '../chat-i18n.js'

/**
 * RoomHeader — panel title bar with avatar, name, presence, search toggle,
 * group settings, leave, and (when can_purge) permanent delete.
 *
 * Props: { store, cable, dockStore? }
 * dockStore — optional dock root store when header is used inside a float panel.
 */
export function RoomHeader({ store, cable, dockStore }) {
  const searchOpen = useSignal(false)
  const leaving    = useSignal(false)
  const purging    = useSignal(false)
  const room       = store.activeRoom.value

  if (!room) return null

  const isDirect    = room.kind === 'direct' || room.type === 'direct'
  const otherPerson = room.other_person   // { id, name, avatar_url } from API
  const name        = room.display_name || room.name || ''
  const memberCount = room.member_count ?? room.members_count ?? null
  const roomId      = room.id
  const canPurge    = !!room.can_purge
  const canManage   = !!store.canManageRooms

  function toggleSearch() {
    searchOpen.value = !searchOpen.value
    if (!searchOpen.value) {
      // Clear search state on close
      store.searchQuery.value  = ''
      store.searchMatches.value = []
      store.searchIdx.value    = 0
    }
  }

  function openProfile() {
    if (!isDirect || !otherPerson) return
    store.profilePerson.value = {
      id:         otherPerson.id,
      name:       name,
      avatar_url: otherPerson.avatar_url ?? null,
    }
  }

  async function handleLeave() {
    if (leaving.value || purging.value) return
    leaving.value = true
    try {
      await leaveRoom({ store, cable, roomId, dockStore })
    } finally {
      leaving.value = false
    }
  }

  async function handlePurge() {
    if (leaving.value || purging.value) return
    purging.value = true
    try {
      await purgeRoom({ store, cable, roomId, dockStore })
    } finally {
      purging.value = false
    }
  }

  return (
    <div class="chat-panel-header">
      {/* Normal header row */}
      {!searchOpen.value && (
        <div class="chat-panel-header-main">
          {/* Avatar — clickable in direct rooms to open contact profile */}
          <div class="chat-panel-header-avatar">
            {isDirect && otherPerson
              ? (
                <button
                  type="button"
                  class="chat-panel-header-avatar-btn"
                  title={name}
                  onClick={openProfile}
                >
                  <HeaderAvatar room={room} name={name} isDirect={isDirect} />
                </button>
              )
              : <HeaderAvatar room={room} name={name} isDirect={isDirect} />
            }
          </div>

          {/* Name + status — clickable in direct rooms */}
          {isDirect && otherPerson
            ? (
              <button
                type="button"
                class="chat-panel-header-info chat-panel-header-info--clickable"
                onClick={openProfile}
              >
                <div class="chat-panel-header-name">{name}</div>
                <div class="chat-panel-header-status">
                  <LastSeen personId={otherPerson.id} store={store} />
                </div>
              </button>
            )
            : (
              <div class="chat-panel-header-info">
                <div class="chat-panel-header-name">{name}</div>
                {memberCount != null && (
                  <div class="chat-panel-header-status">
                    {memberCount} tag
                  </div>
                )}
              </div>
            )
          }

          <div class="chat-panel-header-actions">
            {isDirect && otherPerson && (
              <PresenceDot personId={otherPerson.id} store={store} />
            )}

            {!isDirect && roomId && canManage && (
              <a
                href={edit_chat_room_path(roomId)}
                class="chat-panel-action-btn"
                title={t('chat.rooms.edit_group_title')}
                data-turbo-frame="modal"
                data-turbo-prefetch="false"
              >
                <i class="bi bi-gear" />
              </a>
            )}

            <button
              type="button"
              class="chat-panel-action-btn"
              title={t('chat.messages.search_toggle')}
              onClick={toggleSearch}
            >
              <i class="bi bi-search" />
            </button>

            <button
              type="button"
              class="chat-panel-action-btn chat-panel-action-btn--danger"
              title={t('chat.rooms.leave')}
              disabled={leaving.value || purging.value}
              onClick={handleLeave}
            >
              <i class="bi bi-box-arrow-right" />
            </button>

            {canPurge && (
              <button
                type="button"
                class="chat-panel-action-btn chat-panel-action-btn--danger"
                title={t('chat.rooms.purge')}
                disabled={leaving.value || purging.value}
                onClick={handlePurge}
              >
                <i class="bi bi-trash" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search bar row */}
      {searchOpen.value && (
        <InConversationSearch store={store} onClose={toggleSearch} />
      )}
    </div>
  )
}

function HeaderAvatar({ room, name, isDirect }) {
  // For direct rooms the room itself has no avatar — use the other person's photo.
  const avatarUrl = room.avatar_url || (isDirect ? room.other_person?.avatar_url : null)

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        class={`chat-room-avatar ${isDirect ? 'chat-room-avatar--direct' : 'chat-room-avatar--group'}`}
        width={36}
        height={36}
        onLoad={e => e.target.classList.add('is-loaded')}
        onError={e => { e.target.style.display = 'none' }}
      />
    )
  }

  const initials = (room.avatar_initials || name.slice(0, 1)).toUpperCase() || '?'

  return (
    <div class={`chat-room-avatar chat-room-avatar--initials ${isDirect ? 'chat-room-avatar--direct' : 'chat-room-avatar--group'}`}>
      {isDirect ? initials : <i class="bi bi-people-fill" />}
    </div>
  )
}
